import SimpleCache from '../SimpleCache';
import loadMapsApi from './loadMapsApi';
import { getAutocompleteSessionToken } from './locationAutocomplete';
import { ssFetchJSON } from '../ssFetch';
import { API_ROOT } from '../../config';

const cache = new SimpleCache(60 * 60 * 24); // 1 day in seconds
const searchContainerDiv = document.createElement('div');

/**
 * Get the cache key for geocode data
 *
 * @param {Object} dataToGeocode Same value passed to `geocode`
 * @return {String} Key or undefined if there was an error
 */
function geocodeCacheKey(dataToGeocode) {
  let cacheKey;

  // Convert an object into a collection of sorted key/value arrays
  function objectToSortedArray(obj) {
    if (typeof obj !== 'object') {
      return obj;
    }
    return Object.keys(obj)
      .sort()
      .map(k => {
        const val = obj[k];
        return [k, objectToSortedArray(val)];
      });
  }

  if (!dataToGeocode) {
    return undefined;
  }

  try {
    cacheKey = dataToGeocode;
    if (typeof dataToGeocode === 'object') {
      cacheKey = JSON.stringify(objectToSortedArray(dataToGeocode));
    }
    cacheKey = String(cacheKey)
      .toLowerCase();
  } catch (e) {
    window.console.error('Error converting data to geocode to string', dataToGeocode, e);
  }

  return cacheKey;
}

/**
 * Extracts the latitude, longitude from the provided `GeocoderGeometry`.
 *
 * @param {Object} geometry The GeocodeGeometry from google geocoding API.
 * @returns {Object} An object with the latitude and longitude of the geocided geometry.
 * @see
 * https://developers.google.com/maps/documentation/javascript/reference/geocoder#GeocoderGeometry
 */
function extractCoordinates(geometry) {
  const { location } = geometry;
  if (typeof location.lat === 'function') {
    return {
      latitude: location.lat(),
      longitude: location.lng(),
    };
  }
  return {
    latitude: location.lat,
    longitude: location.lng,
  };
}

/**
 * Normalizes a set of address components to an object containing friendly keys.
 *
 * @param {Array<Object>} addressComponents The address components to normalize.
 * @returns {Object} The normalized address components.
 */
function normalizeAddressComponents(addressComponents) {
  const normalized = {};
  if (!addressComponents) {
    return {};
  }
  // map the address components to their `short_name` or `long_name`, depending on their purpose
  addressComponents.forEach(addressComponent => {
    if (addressComponent.types.length) {
      const type = addressComponent.types[0];
      if (['country', 'administrative_area_level_1'].indexOf(type) !== -1) {
        normalized[type] = addressComponent.short_name;
      } else {
        normalized[type] = addressComponent.long_name;
      }
    }
  });

  // map the keys from google to something a little less vague
  return {
    city: normalized.locality,
    country: normalized.country,
    county: normalized.administrative_area_level_2,
    neighborhood: normalized.neighborhood,
    state: normalized.administrative_area_level_1,
    streetName: normalized.route,
    streetNumber: normalized.street_number,
    zipCode: normalized.postal_code,
  };
}

// Package up data retrieved by places and geocode APIs
const packageData = (region, cacheKey) => {
  const locale = {
    ...extractCoordinates(region.geometry),
    addressComponents: normalizeAddressComponents(region.address_components),
  };
  if (cacheKey) {
    cache.addItem(cacheKey, locale);
  }
  return locale;
};

export const findFirstResult = results => {
  if (results) {
    const region = results.find(result => result.types.includes('neighborhood') || result.types.includes('locality'));
    return region;
  }
  return undefined;
};

/**
 * Gets geocoding information using the SS Geocoding API, based on an address String.
 * @param address A string representing the address.
 */
export function getSSGeocoding(address, cacheKey) {
  return new Promise(resolve => {
    const url = `${API_ROOT}/api/v1/geocoding?location=${address}`;
    ssFetchJSON(url)
      .then(parsed => {
        const { results } = parsed;
        const region = findFirstResult(results);
        const data = packageData(region || (results && results[0]), cacheKey);
        resolve(data);
      });
  });
}

/**
 * Gets geocoding information using the Google geocoding API, based on a lat and lng.
 * @param lat The latitude
 * @param lng The longitude
 */
export async function getGoogleGeocoding(lat, lng, cacheKey) {
  return new Promise((resolve, reject) => {
    loadMapsApi()
      .then(googleAPI => {
        const geocoder = new googleAPI.maps.Geocoder();
        const query = {
          location: {
            lat,
            lng,
          },
        };
        query.location.lat = lat;
        query.location.lng = lng;
        geocoder.geocode(query, (results, status) => {
          if (status !== googleAPI.maps.GeocoderStatus.OK) {
            reject(status);
            return;
          }

          // Get the first result including a locality or neighborhood
          const region = results[0];

          const data = packageData(region || results[0], cacheKey);
          resolve(data);
        });
      });
  });
}

/**
 * Gets geocoding information using the Google Places API, based on a place id.
 * @param placeId The place to Geocode
 */
export function getGooglePlaceGeocoding(placeId, cacheKey) {
  return new Promise((resolve, reject) => {
    loadMapsApi()
      .then(google => {
        const places = new google.maps.places.PlacesService(searchContainerDiv);
        const sessionToken = getAutocompleteSessionToken(true);
        places.getDetails({
          sessionToken,
          placeId,
        }, (results, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            reject(status);
          } else {
            const data = packageData(results, cacheKey);
            resolve(data);
          }
        });
      });
  });
}

/**
 * Query google's geoip API and return StyleSeat normalized location data.
 * @see https://developers.google.com/maps/documentation/javascript/reference/geocoder#Geocoder.geocode
 *
 * The returned object contains:
 *   - lat/lng of the center point
 *   - bounding box: top/left and bottom/right latitude and longitudes.
 *   - addressComponents = The address components for the location
 *
 * @param {String} address A string location (e.g. "San Francisco, Ca")
 * @param {GeocoderRequest} dataToGeocode The query to geocode
 * @yields {Object} Lat/lng coordinates, address components and bounding box for the region.
 */
export default function geocode(dataToGeocode) {
  // Check for invalid requests before hitting the Geo API to prevent unnecessary requests
  const { location } = dataToGeocode;
  const { address } = dataToGeocode;
  const { placeId } = dataToGeocode;
  if (!address && !placeId && !typeof location === 'object') {
    return Promise.reject(window.google.maps.GeocoderStatus.INVALID_REQUEST);
  }

  // Cached value
  const cacheKey = geocodeCacheKey(dataToGeocode);
  const cacheValue = (cacheKey) ? cache.getItem(cacheKey) : null;
  if (cacheValue) {
    return Promise.resolve(cacheValue);
  }

  // Query one of our three options for geocoding:
  // Google Geocode API, Google Places API or SS Geocoding
  // return new Promise((resolve, reject) => {
  // Use the places API, if possible, because the API costs less than geocode
  if (placeId) {
    return getGooglePlaceGeocoding(placeId, cacheKey);
  }
  if (typeof location === 'object' && !address) {
    // For lat-long -> location coding use google...
    return getGoogleGeocoding(
      location.latitude || location.lat,
      location.longitude || location.lng, cacheKey,
    );
  }
  // ...for location -> lat-lon coding use our cached geocoding backend to
  // reduce API calls to google.
  return getSSGeocoding(address, cacheKey);
}
